import React, { useState, useEffect } from "react";
import {
    Modal,
    Button,
    Table,
    Tag,
    Descriptions,
    Radio,
    Space,
    Spin,
    notification
} from "antd";
import { httpGet, customBaseUrl } from "../../../store/http";
import { currencyFormat, formatDate } from "../../../utils/helper";

const BulkBatchDetailsModal = ({ showModal, hideModal, batchRef, onContinue }) => {
    const [loading, setLoading] = useState(false);
    const [batchData, setBatchData] = useState(null);
    const [filterStatus, setFilterStatus] = useState("ALL");

    useEffect(() => {
        if (showModal && batchRef) {
            fetchBatchDetails();
        }
    }, [showModal, batchRef]);

    const fetchBatchDetails = async () => {
        setLoading(true);
        try {
            const url = `${customBaseUrl.tranv2}/api/v1/admin/bulk-disbursement/${batchRef}`;
            const res = await httpGet(url);
            if (res.success) {
                setBatchData(res.data);
            } else {
                notification.error({ message: res.message || "Failed to fetch batch details." });
            }
        } catch (error) {
            notification.error({ message: error.message || "An error occurred while fetching details." });
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = batchData?.items?.filter(item => {
        if (filterStatus === "VALIDATED") return item.validationStatus === "VALIDATED";
        if (filterStatus === "INVALID") return item.validationStatus === "INVALID";
        if (filterStatus === "SUCCESSFUL") return item.disbursementStatus === "SUCCESSFUL";
        if (filterStatus === "FAILED") return item.disbursementStatus === "FAILED";
        return true;
    }) || [];

    const columns = [
        { title: "Row", dataIndex: "rowIndex", key: "rowIndex", width: 60 },
        { title: "Account Number", dataIndex: "accountNumber", key: "accountNumber" },
        { title: "Account Name", dataIndex: "resolvedAccountName", key: "resolvedAccountName", render: (text) => text || "N/A" },
        { title: "Amount", dataIndex: "amount", key: "amount", render: (val) => currencyFormat(val) },
        {
            title: "Validation",
            dataIndex: "validationStatus",
            key: "validationStatus",
            render: (status) => (
                <Tag color={status === "VALIDATED" ? "green" : "red"}>{status}</Tag>
            )
        },
        {
            title: "Disbursement",
            dataIndex: "disbursementStatus",
            key: "disbursementStatus",
            render: (status) => (
                <Tag color={status === "COMPLETED" || status === "SUCCESSFUL" ? "green" : (status === "FAILED" ? "red" : "blue")}>
                    {status}
                </Tag>
            )
        },
        { title: "Message", dataIndex: "disbursementMessage", key: "disbursementMessage", render: (text, record) => text || record.validationMessage },
    ];

    return (
        <Modal
            title={`Batch Details: ${batchRef}`}
            visible={showModal}
            onCancel={hideModal}
            footer={[
                <Button key="close" onClick={hideModal}>Close</Button>,
                batchData && batchData.status !== 'COMPLETED' && batchData.status !== 'FAILED' && (
                    <Button
                        key="continue"
                        type="primary"
                        onClick={() => {
                            hideModal();
                            if (onContinue) onContinue(batchRef);
                        }}
                    >
                        Continue Disbursement
                    </Button>
                )
            ]}
            width={900}
        >
            <Spin spinning={loading}>
                {batchData && (
                    <Space direction="vertical" style={{ width: "100%" }} size="large">
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Batch Reference">{batchData.batchReference}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={batchData.status === "COMPLETED" ? "green" : "blue"}>{batchData.status}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="GL Account">{batchData.glAccountCode}</Descriptions.Item>
                            <Descriptions.Item label="Total Amount">{currencyFormat(batchData.totalAmount)}</Descriptions.Item>
                            <Descriptions.Item label="Total Items">{batchData.totalItems}</Descriptions.Item>
                            <Descriptions.Item label="Successful">{batchData.successfulItems}</Descriptions.Item>
                            <Descriptions.Item label="Failed">{batchData.failedItems}</Descriptions.Item>
                            <Descriptions.Item label="Created At">{formatDate(batchData.createdAt)}</Descriptions.Item>
                            <Descriptions.Item label="Narration" span={2}>{batchData.narration}</Descriptions.Item>
                        </Descriptions>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold' }}>Item Breakdown</span>
                            <Radio.Group value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} size="small">
                                <Radio.Button value="ALL">All</Radio.Button>
                                <Radio.Button value="VALIDATED">Valid</Radio.Button>
                                <Radio.Button value="INVALID">Invalid</Radio.Button>
                                <Radio.Button value="SUCCESSFUL">Successful</Radio.Button>
                                <Radio.Button value="FAILED">Failed</Radio.Button>
                            </Radio.Group>
                        </div>

                        <Table
                            dataSource={filteredItems}
                            columns={columns}
                            rowKey={(record) => `${record.batchReference}-${record.rowIndex}`}
                            size="small"
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: true }}
                        />
                    </Space>
                )}
            </Spin>
        </Modal>
    );
};

export default BulkBatchDetailsModal;
